import cn from 'classnames';
import React, { FC } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  Report,
  ReportType,
} from '@/shared/api/protocol-ts/model/dto_report_pb';
import { Tooth } from '@/shared/api/protocol-ts/model/dto_report_tooth_pb';
import { BWIcons } from '@/shared/assets/images/ToothStatusIcon';
import { DEFAULT_MEDICAL_IMAGE_HEIGHT } from '@/shared/config';
import {
  SelectedSlicesMIR,
  ToothCrops,
} from '@/shared/graphics/medicalImageRender/MedicalImageRender';
import { useAppSelector } from '@/shared/hooks';

import { composedModel } from '@/entities/composed';
import { ConditionColor } from '@/entities/condition';
import { conditionText } from '@/entities/condition/config/i18n';
import { organizationModel } from '@/entities/organization';
import {
  getToothTitleNumber,
  isMissingThirdMolarTooth,
  toothModel,
} from '@/entities/tooth';

import { PreviewFullHeadMprSlices } from '../PreviewFullHeadMprSlices';

import styles from './PreviewToothCard.module.scss';

type PreviewToothCardProps = {
  tooth: Tooth;
  showProbability: boolean;
  showConditions: boolean;
  showMedicalImages: boolean;
  inverted: boolean;
  blackAndWhiteMode: boolean;
  isDICOM: boolean;
  report?: Report;
};

const colorStyle: Record<ConditionColor, string> = {
  yellow: styles.colorYellow,
  red: styles.colorRed,
  purple: styles.colorPurple,
  white: styles.colorWhite,
};

export const PreviewToothCard: FC<PreviewToothCardProps> = (props) => {
  const {
    tooth,
    showProbability,
    showConditions,
    showMedicalImages,
    blackAndWhiteMode,
    inverted,
    report,
  } = props;

  const { formatMessage } = useIntl();

  const dentalNotationFormat = useAppSelector(
    organizationModel.selectors.selectDentalNotationFormat,
  );

  const reportType = report?.Type;

  const conditionItems = useAppSelector(
    composedModel.selectors.selectPreviewCompactToothConditions(tooth.ID),
  );

  const toothStatus = useAppSelector(
    composedModel.selectors.selectToothStatus(tooth.ID, true),
  );

  const displayComment = useAppSelector(
    toothModel.selectors.selectToothCommentForDisplay(tooth.ID),
  );

  const displaySlices = useAppSelector(
    toothModel.selectors.selectDisplaySlicesByToothID(tooth.ID),
  );

  const showToothCard = !!displayComment || showConditions || showMedicalImages;

  const toothNumeration = tooth.Numeration;

  const isMissingThirdMolar = isMissingThirdMolarTooth(
    toothStatus,
    toothNumeration?.ISO ?? 0,
  );

  const shouldShowCroppedImages =
    (reportType === ReportType.ReportType_IOXRay_GP ||
      reportType === ReportType.ReportType_Pano_GP ||
      reportType === ReportType.ReportType_Pano_Bitewings) &&
    showMedicalImages;

  const titleToothNumber = getToothTitleNumber({
    numeration: toothNumeration,
    dentalNotationFormat,
  });

  if (!showToothCard) {
    return null;
  }

  return (
    <div className={styles.container} data-component="preview-tooth-card">
      <div className={styles.toothNumberWrapper}>
        {!isMissingThirdMolar && blackAndWhiteMode && (
          <img
            className={styles.toothStatusIcon}
            src={BWIcons[toothStatus]}
            alt={toothStatus}
          />
        )}

        <h5
          className={cn(
            'h5',
            styles.toothTitle,
            blackAndWhiteMode
              ? styles.blackToothNumber
              : !isMissingThirdMolar && `${toothStatus}ToothNumber`,
          )}
        >
          <FormattedMessage
            id="toothCard.title"
            defaultMessage="Tooth {number}"
            values={{
              number: titleToothNumber,
            }}
          />
        </h5>

        {showConditions &&
          conditionItems.map((item, index) => {
            const selectedChildConditions = item.childConditionInterfaces;

            const showTags = !!selectedChildConditions?.length;

            const isLastCondition = index === conditionItems.length - 1;
            const withChildConditions = selectedChildConditions?.length;

            const childConditionCloseComma = isLastCondition ? `)  ` : `),  `;

            return (
              <React.Fragment key={item.id}>
                <span
                  className={cn(
                    styles.toothCondition,
                    !blackAndWhiteMode &&
                      !isMissingThirdMolar &&
                      colorStyle[item.color],
                    'p2',
                  )}
                >
                  {item.code in conditionText &&
                    formatMessage(conditionText[item.code])}

                  {showProbability && ` ${item.probabilityText}`}

                  {!isLastCondition && !withChildConditions && ', '}
                </span>

                {showTags &&
                  selectedChildConditions.map((tag, tagIndex) => (
                    <span
                      key={tag.id}
                      className={cn(
                        styles.toothCondition,
                        !blackAndWhiteMode &&
                          !isMissingThirdMolar &&
                          colorStyle[tag.color],
                        'p2',
                        styles.childConditions,
                      )}
                    >
                      {tagIndex === 0 ? `  (` : ``}

                      {tag.code in conditionText &&
                        formatMessage(conditionText[tag.code])}

                      {showProbability && ` ${tag.probabilityText}`}

                      {tagIndex !== selectedChildConditions.length - 1
                        ? ',  '
                        : childConditionCloseComma}
                    </span>
                  ))}
              </React.Fragment>
            );
          })}
      </div>

      {displayComment && (
        <p className={cn(styles.comment, 'p2')}>{displayComment}</p>
      )}

      {shouldShowCroppedImages && (
        <ToothCrops
          toothID={tooth.ID}
          className={cn(
            styles.medicalImages,
            blackAndWhiteMode && styles.grayscale,
          )}
          imageAttributes={{
            style: { height: DEFAULT_MEDICAL_IMAGE_HEIGHT + 'px' },
          }}
        />
      )}

      {showMedicalImages && report && (
        <PreviewFullHeadMprSlices
          blackAndWhiteMode={blackAndWhiteMode}
          inverted={inverted}
          report={report}
          toothID={tooth.ID}
          serverSlicesFallback={
            displaySlices.length > 0 && (
              <SelectedSlicesMIR
                toothID={tooth.ID}
                className={cn(
                  styles.medicalImages,
                  blackAndWhiteMode && styles.grayscale,
                )}
                imageAttributes={{
                  style: { height: DEFAULT_MEDICAL_IMAGE_HEIGHT + 'px' },
                }}
              />
            )
          }
        />
      )}

      {showMedicalImages && !report && displaySlices.length > 0 && (
        <SelectedSlicesMIR
          toothID={tooth.ID}
          className={cn(
            styles.medicalImages,
            blackAndWhiteMode && styles.grayscale,
          )}
          imageAttributes={{
            style: { height: DEFAULT_MEDICAL_IMAGE_HEIGHT + 'px' },
          }}
        />
      )}
    </div>
  );
};
